import { useCallback, useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Avatar,
  Typography,
  Box,
  Grid2 as Grid,
} from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
// import { IconButton } from "@mui/material";
import { getSupportTicketById, updateSupportTicketStatusAction } from "../../Actions/SupportTicketAction";
import { MEDIA_URL } from "../../config";
import StatusUpdateModal from "./StatusUpdateModal";
import {
  MyAccessTimeIcon,
  MyAttachFileIcon,
  MyCancelIcon,
  MyCheckCircleIcon,
  MyCircleIcon,
  MyCloseIcon,
  MyDownloadIcon,
  MyPauseCircleIcon,
  MyRefreshIcon,
} from "../../Assets/svg/MyIcons";

dayjs.extend(relativeTime);

// Constants
const STATUS_CONFIG = {
  0: { label: "Pending", color: "default", emoji: "⚪", icon: MyCircleIcon },
  1: {
    label: "In Progress",
    color: "warning",
    emoji: "🟡",
    icon: MyAccessTimeIcon,
  },
  2: { label: "Rejected", color: "error", emoji: "🔴", icon: MyCancelIcon },
  3: {
    label: "Completed",
    color: "success",
    emoji: "🟢",
    icon: MyCheckCircleIcon,
  },
  4: {
    label: "On Hold",
    color: "secondary",
    emoji: "🟣",
    icon: MyPauseCircleIcon,
  },
};

// Utility functions
const formatDate = (date, format = "MMMM D, YYYY [at] h:mm A") => {
  return date ? dayjs(date).format(format) : "";
};

const normalizeArray = (data) => {
  if (Array.isArray(data)) return data;
  if (typeof data === "string" && data.trim()) {
    try {
      return JSON.parse(data);
    } catch {
      return data
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  return [];
};

const isImageFile = (fileName) => {
  return /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(fileName);
};

// Sub-components
const TicketHeader = ({ ticket, onRefresh, onClose }) => (
  <DialogTitle sx={{ bgcolor: "background.paper", p: 3 }}>
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box flex={1}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="h5" component="span">
            {ticket?.title || "Ticket Details"}
          </Typography>
          {ticket && (
            <Chip
              label={`${STATUS_CONFIG[ticket.status]?.emoji || ""} ${STATUS_CONFIG[ticket.status]?.label || "Unknown"
                }`}
              size="small"
              color={STATUS_CONFIG[ticket.status]?.color || "default"}
            />
          )}
        </Box>
        {ticket && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Created {formatDate(ticket.createdAt)}
          </Typography>
        )}
      </Box>

      <Box display="flex" gap={1} alignItems="center">
        <IconButton onClick={onRefresh} title="Refresh" size="small">
          <MyRefreshIcon size={18} />
        </IconButton>
        <Button onClick={onClose} size="small">
          <MyCloseIcon />
        </Button>
      </Box>
    </Box>
  </DialogTitle>
);

const TicketDescription = ({ description }) => (
  <Box mb={3}>
    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
      Description
    </Typography>
    <Box
      sx={{
        bgcolor: "grey.50",
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
      }}
    >
      <Typography
        variant="body2"
        sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
      >
        {description || "No description provided"}
      </Typography>
    </Box>
  </Box>
);

const AttachmentItem = ({ fileName, fileUrl, onDownload, isDownloading }) => {
  const isImage = isImageFile(fileName);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: "primary.main",
          bgcolor: "action.hover",
        },
      }}
    >
      {isImage ? (
        <Avatar
          variant="rounded"
          src={fileUrl}
          sx={{ width: 48, height: 48 }}
        />
      ) : (
        <Box
          sx={{
            width: 48,
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.100",
            borderRadius: 1,
          }}
        >
          <MyAttachFileIcon size={20} />
        </Box>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
          {fileName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {isImage ? "Image" : "File"}
        </Typography>
      </Box>

      <IconButton
        onClick={() => onDownload(fileUrl, fileName)}
        disabled={isDownloading}
        size="small"
      >
        {isDownloading ? (
          <CircularProgress size={16} />
        ) : (
          <MyDownloadIcon size={16} />
        )}
      </IconButton>
    </Box>
  );
};

const TicketAttachments = ({
  attachments,
  baseUrl,
  onDownload,
  downloadingStatus,
}) => {
  if (!Array.isArray(attachments) || attachments.length === 0) return null;

  return (
    <Box mb={3}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
        Attachments ({attachments.length})
      </Typography>
      <Grid container spacing={2}>
        {attachments.map((fileName, idx) => {
          const fileUrl = `${baseUrl}/${fileName}`;
          return (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <AttachmentItem
                fileName={fileName}
                fileUrl={fileUrl}
                onDownload={(url, name) => onDownload(url, name, idx)}
                isDownloading={!!downloadingStatus[idx]}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

const CommentAttachments = ({ attachments, onDownload, downloadingStatus }) => {
  if (!Array.isArray(attachments) || attachments.length === 0) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        Attachments ({attachments.length})
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {attachments.map((attachment, idx) => {
          const isImage = isImageFile(attachment.fileName || attachment.originalFileName);
          const fileName = attachment.originalFileName || attachment.fileName;
          const fileUrl = attachment.fileUrl;

          return (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
            >
              {isImage ? (
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 1.5,
                    overflow: "hidden",
                    bgcolor: "grey.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={fileUrl}
                    alt={fileName}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <Box
                    sx={{
                      display: "none",
                      width: "100%",
                      height: "100%",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <MyAttachFileIcon size={24} />
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "grey.100",
                    borderRadius: 1.5,
                  }}
                >
                  <MyAttachFileIcon size={24} />
                </Box>
              )}

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500, mb: 0.5 }}>
                  {fileName}
                </Typography>
                {attachment.fileSize && (
                  <Typography variant="caption" color="text.secondary">
                    {(attachment.fileSize / 1024).toFixed(1)} KB
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: "flex", gap: 0.5 }}>
                {isImage && (
                  <IconButton
                    onClick={() => window.open(fileUrl, '_blank')}
                    size="small"
                    sx={{
                      bgcolor: "action.hover",
                      "&:hover": { bgcolor: "action.selected" }
                    }}
                  >
                    <MyCircleIcon size={16} />
                  </IconButton>
                )}
                <IconButton
                  onClick={() => onDownload(fileUrl, fileName, idx)}
                  disabled={!!downloadingStatus[idx]}
                  size="small"
                  sx={{
                    bgcolor: "action.hover",
                    "&:hover": { bgcolor: "action.selected" }
                  }}
                >
                  {downloadingStatus[idx] ? (
                    <CircularProgress size={16} />
                  ) : (
                    <MyDownloadIcon size={16} />
                  )}
                </IconButton>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const CommentItem = ({ comment, onDownloadAttachment, downloadingStatus }) => {
  const statusConfig = STATUS_CONFIG[comment.currentStatus] || STATUS_CONFIG[0];
  const isUser = comment.sender === 'user';
  const isStatusChange = comment.isStatusChange;

  // Status change with optional comment - Grouped design
  if (isStatusChange) {
    return (
      <Box sx={{ mb: 3 }}>
        {/* Status Change Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            bgcolor: statusConfig.color + ".50",
            border: 1,
            borderColor: statusConfig.color + ".main",
            borderRadius: 2,
            p: 2,
            mb: comment.text ? 1 : 0,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: statusConfig.color + ".main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: 18,
            }}
          >
            📋
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Status changed to{" "}
              <Chip
                label={statusConfig.label}
                size="small"
                color={statusConfig.color}
                sx={{ ml: 0.5, fontWeight: 600 }}
              />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(comment.timestamp || comment.createdAt, "MMM D, h:mm A")}
            </Typography>
          </Box>
        </Box>

        {/* Associated Comment (if exists) */}
        {comment.text && comment.text.trim() && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
              ml: 2,
              pl: 2,
              borderLeft: 3,
              borderColor: statusConfig.color + ".main",
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: isUser ? "primary.main" : "success.main",
                fontSize: 12,
              }}
            >
              {isUser ? "👤" : "🛠️"}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary", mb: 0.5, display: "block" }}>
                {isUser ? "Client User" : "Support Team"} added a note:
              </Typography>
              <Box
                sx={{
                  bgcolor: "grey.100",
                  p: 1.5,
                  borderRadius: 1.5,
                  border: 1,
                  borderColor: "grey.300",
                }}
              >
                <Typography variant="body2" sx={{ lineHeight: 1.4, color: "text.primary" }}>
                  {comment.text}
                </Typography>
                <CommentAttachments
                  attachments={comment.attachments}
                  onDownload={onDownloadAttachment}
                  downloadingStatus={downloadingStatus}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  // Regular standalone comments (rare case)
  return (
    <Box sx={{ display: "flex", justifyContent: isUser ? "flex-start" : "flex-end", mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          maxWidth: "75%",
          flexDirection: isUser ? "row" : "row-reverse",
        }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: isUser ? "primary.main" : "success.main",
            fontSize: 14,
          }}
        >
          {isUser ? "👤" : "🛠️"}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 0.5,
            flexDirection: isUser ? "row" : "row-reverse",
            gap: 1
          }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary" }}>
              {isUser ? "Client User" : "Support Team"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(comment.timestamp || comment.createdAt, "MMM D, h:mm A")}
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: isUser ? "primary.main" : "success.main",
              color: "white",
              p: 1.5,
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
              {comment.text}
            </Typography>
            <CommentAttachments
              attachments={comment.attachments}
              onDownload={onDownloadAttachment}
              downloadingStatus={downloadingStatus}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const StatusUpdates = ({ comments, onDownloadAttachment, downloadingStatus }) => {
  const scrollContainerRef = useRef(null);

  // Scroll to bottom whenever comments change
  useEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [comments]);

  // Also scroll to bottom on initial load with a slight delay to ensure content is rendered
  useEffect(() => {
    if (scrollContainerRef.current && comments && comments.length > 0) {
      const container = scrollContainerRef.current;
      setTimeout(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [comments]);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
        Activity Timeline
      </Typography>
      <Box
        ref={scrollContainerRef}
        sx={{
          bgcolor: "#f8fafc",
          p: 2.5,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          maxHeight: "500px",
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "grey.300",
            borderRadius: "3px",
            "&:hover": {
              bgcolor: "grey.400",
            },
          },
        }}
      >
        {!Array.isArray(comments) || comments.length === 0 ? (
          <Box textAlign="center" py={6}>
            <MyCircleIcon size={48} color="#cbd5e1" strokeWidth={1} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No activity yet
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {comments.map((comment, index) => (
              <CommentItem
                key={comment.id || index}
                comment={comment}
                onDownloadAttachment={onDownloadAttachment}
                downloadingStatus={downloadingStatus}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const InfoRow = ({ label, value, mono = false }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        display: "block",
        mb: 0.5,
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, ...(mono && { fontFamily: "monospace" }) }}
    >
      {value}
    </Typography>
  </Box>
);

const TicketSidebar = ({ ticket, onUpdateStatus, permission }) => {
  const statusConfig = STATUS_CONFIG[ticket?.status] || STATUS_CONFIG[0];
  return (
    <Box sx={{ position: "sticky", top: 0 }}>
      <Box
        sx={{
          bgcolor: "background.paper",
          p: 3,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          mb: 2,
          boxShadow: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ fontWeight: 600, mb: 3 }}
        >
          Ticket Information
        </Typography>

        <InfoRow
          label="Ticket ID"
          value={`#${ticket?.ticketCode || "N/A"}`}
          mono
        />

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mb: 1,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontWeight: 600,
            }}
          >
            Current Status
          </Typography>
          <Chip
            label={`${statusConfig.emoji} ${statusConfig.label}`}
            size="medium"
            color={statusConfig.color}
            sx={{ fontWeight: 600, fontSize: "0.875rem" }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <InfoRow label="Created" value={formatDate(ticket?.createdAt, "MMM D, YYYY")} />
        <InfoRow label="Last Updated" value={formatDate(ticket?.updatedAt, "MMM D, YYYY")} />

        <Divider sx={{ my: 2 }} />

        <InfoRow
          label="Priority"
          value={ticket?.priority || "Normal"}
        />
      </Box>

      {permission.canEdit &&
        <Box
          sx={{
            bgcolor: "background.paper",
            p: 2.5,
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            boxShadow: 1,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={onUpdateStatus}
            startIcon={<MyRefreshIcon size={18} />}
            sx={{ py: 1.5 }}
          >
            Update Status
          </Button>
        </Box>
      }
    </Box>
  );
};

// Main component
const SupportTicketModel = ({
  open,
  handleClose,
  ticketData,
  refetchSupportTickets,
  setSnackbar,
  permission
}) => {
  const [ticket, setTicket] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingStatus, setDownloadingStatus] = useState({});
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [chatMessage, setChatMessage] = useState("");

  const showSnackbar = useCallback(
    (severity, message) => {
      setSnackbar?.({ open: true, severity, message });
    },
    [setSnackbar]
  );

  const fetchTicketDetails = useCallback(async () => {
    if (!ticketData?.id) return;

    setIsLoading(true);
    try {
      const response = await getSupportTicketById(ticketData.id);

      if (response?.success) {
        const data = response.data || {};
        setTicket({
          ...data,
          attachments: normalizeArray(data.attachments),
          comments: normalizeArray(data.comments),
        });
      } else {
        showSnackbar(
          "error",
          response?.message || "Failed to load ticket details."
        );
      }
    } catch (err) {
      console.error("Failed to fetch ticket details:", err);
      showSnackbar("error", "An error occurred while fetching ticket details.");
    } finally {
      setIsLoading(false);
    }
  }, [ticketData, showSnackbar]);

  useEffect(() => {
    if (open && ticketData) {
      fetchTicketDetails();
    }
  }, [open, ticketData, fetchTicketDetails]);

  const handleDownloadAttachment = async (fileUrl, fileName, key) => {
    try {
      setDownloadingStatus((prev) => ({ ...prev, [key]: true }));
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("Failed to fetch file");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      showSnackbar("success", `Downloaded ${fileName}`);
    } catch (err) {
      console.error("Download failed:", err);
      showSnackbar("error", "Download failed. Please try again.");
    } finally {
      setDownloadingStatus((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleStatusUpdated = () => {
    setShowStatusModal(false);
    fetchTicketDetails();
    refetchSupportTickets?.();
  };
  const handleSendMessage = async () => {
    const trimmed = chatMessage.trim();

    const formData = new FormData();
    formData.append('id', ticketData.id);
    formData.append('status', ticketData.status);
    formData.append('comment', trimmed);
    try {
      const response = await updateSupportTicketStatusAction(formData, true); // true indicates multipart form data

      if (!response?.success) {
        throw new Error(response?.message || "Failed to update status");
      }
      setChatMessage("");
    } catch (error) {
      console.error("Status update failed:", error);
      // setErrors({
      //   submit: error.message || "Failed to update status. Please try again.",
      // });
    } finally {
      handleStatusUpdated();
      refetchSupportTickets();
      // setSubmitting(false);
    }
    // setChatMessage("");
  };

  if (!open) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        slotProps={{ paper: { sx: { height: "90vh" } } }}
      >
        <TicketHeader
          ticket={ticket}
          onRefresh={fetchTicketDetails}
          onClose={handleClose}
        />

        <Divider />

        <DialogContent dividers sx={{ p: 3, overflow: "auto" }}>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="60vh"
            >
              <CircularProgress />
            </Box>
          ) : ticket ? (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, lg: 9 }}>
                <TicketDescription description={ticket.description} />
                <TicketAttachments
                  attachments={ticket.attachments}
                  baseUrl={`${MEDIA_URL}/${ticket.domainUrl}/supportTickets`}
                  onDownload={handleDownloadAttachment}
                  downloadingStatus={downloadingStatus}
                />
                <StatusUpdates
                  comments={ticket.comments}
                  onDownloadAttachment={handleDownloadAttachment}
                  downloadingStatus={downloadingStatus}
                />

                {/* ── Chat Input Box ─────────────────────────────────── */}
                <Box
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    bgcolor: "background.default",
                    pt: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "20px",
                      bgcolor: "#fff",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: "primary.main",
                        fontSize: 15,
                      }}
                    >
                      👤
                    </Avatar>

                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        borderRadius: "18px",
                        bgcolor: "#f8fafc",
                        px: 2,
                        py: 1,
                        transition: "all .2s ease",
                        border: "1px solid transparent",

                        "&:focus-within": {
                          bgcolor: "#fff",
                          borderColor: "primary.main",
                          boxShadow: "0 0 0 4px rgba(25,118,210,0.08)",
                        },
                      }}
                    >
                      <textarea
                        value={chatMessage}
                        onChange={(e) => setChatMessage(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        placeholder="Message..."
                        rows={1}
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          resize: "none",
                          background: "transparent",
                          fontSize: "14px",
                          fontFamily: "inherit",
                          lineHeight: "22px",
                          minHeight: "24px",
                          maxHeight: "120px",
                          overflowY: "auto",
                        }}
                      />
                    </Box>

                    <IconButton
                      color="primary"
                      onClick={handleSendMessage}
                      disabled={!chatMessage.trim()}
                      sx={{
                        width: 46,
                        height: 46,
                        bgcolor: "primary.main",
                        color: "#fff",

                        "&:hover": {
                          bgcolor: "primary.dark",
                        },

                        "&.Mui-disabled": {
                          bgcolor: "#e5e7eb",
                          color: "#94a3b8",
                        },
                      }}
                    >
                      <SendRoundedIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, lg: 3 }}>
                <TicketSidebar
                  ticket={ticket}
                  onUpdateStatus={() => setShowStatusModal(true)}
                  permission={permission}
                />
              </Grid>
            </Grid>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="60vh"
            >
              <Typography variant="body1" color="text.secondary">
                No ticket data available
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {showStatusModal && ticket && (
        <StatusUpdateModal
          ticketData={ticketData}
          currentStatus={ticket.status}
          open={showStatusModal}
          onOpenChange={setShowStatusModal}
          onStatusUpdated={handleStatusUpdated}
          refetchSupportTickets={refetchSupportTickets}
        />
      )}
    </>
  );
};

export default SupportTicketModel;
